import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Terminal, Copy, Check, ChevronDown, Sparkles, KeyRound, Loader2,
  ExternalLink, ShieldCheck, Wand2
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Card "Conecte suas campanhas ao seu assistente de IA" da tela de Campanhas.
 *
 * Reaproveita o mesmo servidor MCP exposto em Configurações → API, mas resolve o
 * atrito que existe lá: a chave completa só existe no momento da criação (é
 * hasheada no servidor), então quem volta na página só vê "SUA_API_KEY". Aqui o
 * usuário cria a chave em um clique e já recebe o comando pronto pra colar.
 */

const MCP_SERVER_URL = 'https://marketing-api.devskin.com/api/mcp/rpc';

const KEY_PLACEHOLDER = 'SUA_API_KEY';

const claudeCmd = (key: string) =>
  `claude mcp add --transport http devskin-marketing ${MCP_SERVER_URL} --header "Authorization: Bearer ${key}"`;

const jsonConfig = (key: string) => `{
  "mcpServers": {
    "devskin-marketing": {
      "type": "http",
      "url": "${MCP_SERVER_URL}",
      "headers": { "Authorization": "Bearer ${key}" }
    }
  }
}`;

type ClientId = 'claude' | 'codex' | 'cursor';

const STORAGE_KEY = 'campaigns.mcpCard.collapsed';

const McpConnectCard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Mesma regra da aba Configurações → API: funcionário comum não gerencia chave
  // (a chave age como o dono da conta). Sem isto o card viraria um atalho pra furar
  // a permissão que a tela de Configurações já nega.
  const canManageKeys = !user?.ownerUserId || user?.employeeRole === 'admin';

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [client, setClient] = useState<ClientId>('claude');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* localStorage indisponível (modo privado) — só perde a preferência */
    }
  }, [collapsed]);

  // Evita setState depois do unmount quando o usuário sai da página logo após copiar.
  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const key = generatedKey || KEY_PLACEHOLDER;
  const hasKey = !!generatedKey;

  const snippet = useMemo(
    () => (client === 'claude' ? claudeCmd(key) : jsonConfig(key)),
    [client, key]
  );

  const copy = useCallback(async (text: string, id: string) => {
    try {
      // clipboard API exige HTTPS + permissão; cai no fallback quando indisponível.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(id);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error(t('campaigns.mcp.copyFailed', 'Não foi possível copiar. Selecione o texto e copie manualmente.'));
    }
  }, [t]);

  const generateKey = useCallback(async () => {
    if (isGenerating) return; // trava duplo clique — cada clique cria uma chave nova
    setIsGenerating(true);
    try {
      const name = `MCP — Campanhas (${new Date().toLocaleDateString('pt-BR')})`;
      const res = await api.post('/api-keys', { name, expires_in_days: 0, scopes: ['read', 'write'] });
      const full = res?.data?.data?.key;
      if (res?.data?.success && full) {
        setGeneratedKey(full);
        toast.success(t('campaigns.mcp.keyCreated', 'Chave criada! O comando abaixo já está pronto pra colar.'));
      } else {
        toast.error(t('campaigns.mcp.keyFailed', 'Não foi possível criar a chave. Tente por Configurações → API.'));
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        t('campaigns.mcp.keyFailed', 'Não foi possível criar a chave. Tente por Configurações → API.')
      );
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, t]);

  const clients: Array<{ id: ClientId; label: string }> = [
    { id: 'claude', label: 'Claude Code' },
    { id: 'codex', label: 'Codex' },
    { id: 'cursor', label: 'Cursor' },
  ];

  const examples = [
    t('campaigns.mcp.ex1', '"Quais campanhas gastaram mais de R$ 500 e não converteram?"'),
    t('campaigns.mcp.ex2', '"Cria uma campanha de Google Ads pro meu curso, orçamento R$ 80/dia"'),
    t('campaigns.mcp.ex3', '"Pausa tudo que está com ROAS abaixo de 1"'),
    t('campaigns.mcp.ex4', '"Me dá 5 títulos novos pro anúncio com menor CTR"'),
  ];

  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Fio de luz no topo — dá o toque "IA" sem pesar o card */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/70 to-transparent" />
      {/* Brilho difuso no canto, puramente decorativo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-primary-500/10 blur-3xl"
      />

      <div className="relative">
        {/* Cabeçalho — clicável inteiro para abrir/fechar */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="w-full flex items-start sm:items-center gap-3 sm:gap-4 p-5 text-left hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors"
        >
          <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-purple-600 text-white shadow-sm">
            <Terminal className="w-5 h-5" />
          </span>

          <span className="flex-1 min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                {t('campaigns.mcp.title', 'Gerencie estas campanhas pelo seu assistente de IA')}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                <Sparkles className="w-3 h-3" />
                MCP
              </span>
            </span>
            <span className="block mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              {t(
                'campaigns.mcp.subtitle',
                'Conecte o Claude Code, Codex ou Cursor e peça em português: criar campanha, pausar, ajustar orçamento e analisar resultados — sem sair do seu editor.'
              )}
            </span>
          </span>

          <ChevronDown
            className={`shrink-0 w-5 h-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
          />
        </button>

        {!collapsed && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 dark:border-gray-700/60 pt-4">
            {/* Passo 1 — chave */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-900 dark:bg-gray-600 text-white text-[10px] font-bold">1</span>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('campaigns.mcp.step1', 'Gere sua chave de acesso')}
                </h4>
              </div>

              {hasKey ? (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3">
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                        {t('campaigns.mcp.keyReady', 'Chave criada e já embutida no comando abaixo.')}
                      </p>
                      <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">
                        {t('campaigns.mcp.keyWarning', 'Ela só aparece agora — copie o comando antes de sair da página. Se perder, é só gerar outra.')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : !canManageKeys ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {t('campaigns.mcp.needOwner', 'Só o dono da conta (ou um membro com papel de administrador) pode gerar a chave de acesso. Peça a chave a ele e cole no comando abaixo.')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <button
                    type="button"
                    onClick={generateKey}
                    disabled={isGenerating}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                    {isGenerating
                      ? t('campaigns.mcp.generating', 'Gerando...')
                      : t('campaigns.mcp.generate', 'Gerar chave de acesso')}
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('campaigns.mcp.orExisting', 'Já tem uma chave?')}{' '}
                    <Link to="/settings/api" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                      {t('campaigns.mcp.useExisting', 'usar a minha')}
                    </Link>
                  </span>
                </div>
              )}
            </div>

            {/* Passo 2 — comando */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-900 dark:bg-gray-600 text-white text-[10px] font-bold">2</span>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('campaigns.mcp.step2', 'Cole no seu assistente')}
                </h4>
              </div>

              <div className="inline-flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-900/60 mb-2">
                {clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setClient(c.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      client === c.id
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <div className="relative group">
                <pre className="p-3 pr-12 bg-gray-900 dark:bg-black/60 text-gray-100 rounded-lg text-[11px] leading-relaxed font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {snippet}
                </pre>
                <button
                  type="button"
                  onClick={() => copy(snippet, 'snippet')}
                  title={t('campaigns.mcp.copy', 'Copiar') as string}
                  aria-label={t('campaigns.mcp.copy', 'Copiar') as string}
                  className="absolute top-2 right-2 p-2 rounded-md bg-white/10 hover:bg-white/20 text-gray-200 transition-colors"
                >
                  {copied === 'snippet' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              {!hasKey && (
                <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                  {t('campaigns.mcp.replaceKey', 'Troque SUA_API_KEY pela sua chave — ou clique em "Gerar chave de acesso" acima que ela entra sozinha aqui.')}
                </p>
              )}
            </div>

            {/* Passo 3 — o que pedir */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-900 dark:bg-gray-600 text-white text-[10px] font-bold">3</span>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('campaigns.mcp.step3', 'Peça o que quiser')}
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {examples.map((ex, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60"
                  >
                    <Wand2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-300">{ex}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                {t('campaigns.mcp.footnote', 'Campanhas criadas por IA nascem como rascunho — nada vai ao ar sem você aprovar.')}
              </p>
              {canManageKeys && (
                <Link
                  to="/settings/api"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t('campaigns.mcp.fullDocs', 'Configurações avançadas')}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default McpConnectCard;
